// src/store/builders/useProjectInfoStore.js
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { axiosInstancev1 } from "../../lib/axiosInstance";
import { toast } from "react-hot-toast";
import useAuthStore from "../useAuthStore";

const useProjectInfoStore = create(
  persist(
    (set, get) => ({
      projectInfo: null, // Existing
      masterData: null, // <--- New state variable for master data
      holdComments: [], // Existing
      isLoading: false,
      error: null,

      // --- NEW FUNCTION: Fetch Project Master Data ---
      fetchProjectMasterData: async (projectId) => {
        set({ isLoading: true, error: null, masterData: null }); // Reset master data

        try {
          const { token } = useAuthStore.getState();
          if (!token) throw new Error("Authentication token missing!");

          const response = await axiosInstancev1.get(
            `/project-master/${projectId}`, // Use the new endpoint
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );

          if (response.data.success) {
            // Store the entire 'data' object which contains project, customer, builder, messages
            set({ masterData: response.data.data });
            return response.data.data; // Return the fetched data
          } else {
            throw new Error(response.data.message || "Failed to fetch master data")
          }

        } catch (error) {
          console.error("Fetch Project Master Data Error:", error);
          const errorMessage =
            error.response?.data?.message ||
            "Failed to fetch project master data";
          set({ error: errorMessage });
          toast.error(errorMessage);
          return null; // Indicate failure
        } finally {
          set({ isLoading: false });
        }
      },
      // --- End NEW FUNCTION ---


      // Existing functions (fetchProjectInfo, getHoldComments) remain unchanged
      fetchProjectInfo: async (projectId) => {
        set({ isLoading: true, error: null });
        try {
          const { token } = useAuthStore.getState();
          if (!token) throw new Error("Authentication token missing!");
          const response = await axiosInstancev1.get(
            `/projects/${projectId}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (response.data.success) {
            const { holds, ...projectDetails } = response.data.project;
            set({
              projectInfo: projectDetails,
              holdComments: holds || []
            });
            return response.data.project;
          }
        } catch (error) {
          console.error("Fetch Project Info Error:", error);
          const errorMessage = error.response?.data?.message || "Failed to fetch project information";
          set({ error: errorMessage });
          toast.error(errorMessage);
        } finally {
          set({ isLoading: false });
        }
      },
      getHoldComments: async (projectId) => {
        set({ isLoading: true, error: null });
        try {
          const { token } = useAuthStore.getState();
          if (!token) throw new Error("Authentication token missing!");
          const response = await axiosInstancev1.get(
            `/projects/${projectId}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (response.data.success) {
            set({ holdComments: response.data.project.holds || [] });
            return response.data.project.holds;
          }
        } catch (error) {
          console.error("Fetch Hold Comments Error:", error);
          const errorMessage = error.response?.data?.message || "Failed to fetch hold comments";
          set({ error: errorMessage });
          toast.error(errorMessage);
        } finally {
          set({ isLoading: false });
        }
      }
    }),
    {
      name: "project-info-storage",
      getStorage: () => localStorage,
      partialize: (state) => {
        // Only persist parts needed across sessions, maybe not masterData?
        const { masterData, ...rest } = state;
        return rest;
      },
    }
  )
);

export default useProjectInfoStore;